import { AnalysisResult } from '../types/analysis';

/**
 * Icon priority based on size and type
 * Higher value = higher priority
 */
const ICON_PRIORITY = {
  "apple-touch-icon": 6,     // Apple touch icons are usually high quality
  "icon-512": 7,             // Highest resolution PWA icon
  "icon-256": 6.8,           // Higher resolution PWA icon
  "icon-192": 6.5,           // PWA icons are good quality and sized well
  "icon": 4,                 // Generic icon
  "shortcut icon": 2,        // Shortcut icon
  "favicon": 1,              // Basic favicon
};

/**
 * Helper function to extract the best favicon URL from HTML content
 */
export function extractFaviconUrl(htmlContent: string, baseUrl: string): { 
  url: string | null; 
  size?: string;
  type?: string;
  highRes?: boolean;
} {
  // Find all link tags
  const linkRegex = /<link[^>]*>/gi;
  const links = htmlContent.match(linkRegex) || [];
  
  // Keep track of favicons with their priority
  const favicons: Array<{
    href: string;
    rel: string;
    sizes?: string;
    type?: string;
    priority: number;
  }> = [];
  
  // First pass: collect all potential favicons
  for (const link of links) {
    // Check if it's a favicon-related link
    const relMatch = link.match(/rel=["']([^"']*)["']/i);
    if (!relMatch) continue;
    
    const rel = relMatch[1].toLowerCase();
    
    // Only consider favicon-related rel attributes and ignore social media images
    if (!(
      rel.includes('icon') || 
      rel === 'shortcut icon' || 
      rel === 'apple-touch-icon' || 
      rel === 'apple-touch-icon-precomposed' || 
      rel === 'mask-icon'
    )) {
      continue;
    }
    
    // Skip social media tags which are commonly confused with favicons
    if (rel.includes('og:') || rel.includes('twitter:')) {
      continue;
    }
    
    // Get the href
    const hrefMatch = link.match(/href=["']([^"']*)["']/i);
    if (!hrefMatch) continue;
    
    const href = hrefMatch[1];
    
    // Get sizes if available
    let sizes: string | undefined;
    const sizesMatch = link.match(/sizes=["']([^"']*)["']/i);
    if (sizesMatch) {
      sizes = sizesMatch[1];
    }
    
    // Get type if available
    let type: string | undefined;
    const typeMatch = link.match(/type=["']([^"']*)["']/i);
    if (typeMatch) {
      type = typeMatch[1];
    }
    
    // Calculate priority based on rel, size, and type
    let priority = 0;
    
    // Base priority from relation type
    for (const [key, value] of Object.entries(ICON_PRIORITY)) {
      if (rel.includes(key)) {
        priority = value;
        break;
      }
    }
    
    // Adjust priority based on size (prefer larger icons)
    if (sizes) {
      // Parse the size (e.g., "16x16", "32x32", etc.)
      const sizeMatch = sizes.match(/(\d+)x(\d+)/);
      if (sizeMatch) {
        const size = parseInt(sizeMatch[1]);
        // Increase priority for larger icons
        if (size >= 192) priority += 2;
        else if (size >= 128) priority += 1.5;
        else if (size >= 64) priority += 1;
        else if (size >= 32) priority += 0.5;
      }
    }
    
    // Adjust priority based on filename patterns
    if (href.includes('192')) priority += 0.4;
    if (href.includes('128')) priority += 0.3;
    if (href.includes('64')) priority += 0.2;
    if (href.includes('32')) priority += 0.1;
    
    // Adjust priority based on file type
    if (type) {
      if (type.includes('png')) priority += 0.8;
      else if (type.includes('svg')) priority += 1.0;
      else if (type.includes('webp')) priority += 0.9;
      else if (type.includes('ico')) priority += 0.2;
    } else {
      // If no type specified, guess from extension
      if (href.endsWith('.png')) priority += 0.8;
      else if (href.endsWith('.svg')) priority += 1.0;
      else if (href.endsWith('.webp')) priority += 0.9;
      else if (href.endsWith('.ico')) priority += 0.2;
    }
    
    // Add to favicons array
    favicons.push({
      href,
      rel,
      sizes,
      type,
      priority
    });
  }
  
  // Sort favicons by priority (highest first)
  favicons.sort((a, b) => b.priority - a.priority);
  
  // No favicons found, try default location
  if (favicons.length === 0) {
    try {
      const urlObj = new URL(baseUrl);
      return { 
        url: `${urlObj.protocol}//${urlObj.hostname}/favicon.ico`,
        type: 'image/x-icon'
      };
    } catch (e) {
      console.error('Error creating default favicon URL:', e);
      return { url: null };
    }
  }
  
  // Get the highest priority favicon
  const bestFavicon = favicons[0];
  
  // Resolve URL
  let faviconUrl = bestFavicon.href;
  try {
    if (faviconUrl.startsWith('/')) {
      const urlObj = new URL(baseUrl);
      faviconUrl = `${urlObj.protocol}//${urlObj.hostname}${faviconUrl}`;
    } else if (!faviconUrl.startsWith('http')) {
      faviconUrl = new URL(faviconUrl, baseUrl).href;
    }
  } catch (e) {
    console.error('Error resolving favicon URL:', e);
    return { url: null };
  }
  
  // Determine if it's high resolution
  let highRes = false;
  if (bestFavicon.sizes) {
    const sizeMatch = bestFavicon.sizes.match(/(\d+)x(\d+)/);
    if (sizeMatch && parseInt(sizeMatch[1]) >= 128) {
      highRes = true;
    }
  } else if (bestFavicon.href.includes('192') || bestFavicon.href.includes('256') || bestFavicon.href.includes('512')) {
    highRes = true;
  }
  
  return {
    url: faviconUrl,
    size: bestFavicon.sizes,
    type: bestFavicon.type,
    highRes
  };
}

/**
 * Helper function to normalize API analysis data to ensure consistent structure
 * Used by both direct analysis and history loading flows
 */
export function normalizeAnalysisData(data: any, requestId: string): AnalysisResult {
  // IMPORTANT: We need to check if data has a 'result' property
  // This is the case with cached responses where the actual data is inside result
  const sourceData = data.result || data;
  
  // Log the structure to help with debugging
  console.log('Data structure:', {
    hasResult: !!data.result,
    topLevelKeys: Object.keys(data),
    resultKeys: data.result ? Object.keys(data.result) : 'no result object',
    sourceDataKeys: Object.keys(sourceData)
  });
  
  // Debug sales snippets specifically
  console.log('Sales snippets debug:', {
    hasSummary: !!sourceData.summary,
    summaryKeys: sourceData.summary ? Object.keys(sourceData.summary) : 'no summary',
    painPointsType: sourceData.summary?.painPoints ? typeof sourceData.summary.painPoints : 'not present',
    painPointsIsArray: Array.isArray(sourceData.summary?.painPoints),
    painPointsLength: Array.isArray(sourceData.summary?.painPoints) ? sourceData.summary.painPoints.length : 'n/a',
    painPoints: sourceData.summary?.painPoints,
    jobsToBeDone: sourceData.summary?.jobsToBeDone,
    hasPersonaCentricBenefits: sourceData.summary?.personaCentricBenefits ? Object.keys(sourceData.summary.personaCentricBenefits).length > 0 : false,
    personaCentricBenefitsKeys: sourceData.summary?.personaCentricBenefits ? Object.keys(sourceData.summary.personaCentricBenefits) : [],
    socialProof: sourceData.summary?.socialProof,
    hasOffersAndGuarantees: Array.isArray(sourceData.summary?.offersAndGuarantees),
    offersAndGuaranteesLength: Array.isArray(sourceData.summary?.offersAndGuarantees) ? sourceData.summary.offersAndGuarantees.length : 'n/a',
    hasPriorityKnowledge: Array.isArray(sourceData.summary?.priorityKnowledge),
    priorityKnowledgeLength: Array.isArray(sourceData.summary?.priorityKnowledge) ? sourceData.summary.priorityKnowledge.length : 'n/a',
    commitmentLevel: sourceData.summary?.commitmentLevel
  });

  // Extract business name from different possible locations in priority order
  let businessName = 
    // Try the summary object in the source data
    sourceData.summary?.businessName || 
    // Try businessDetails in the source data
    sourceData.businessDetails?.name ||
    sourceData.businessDetails?.companyName || 
    // Check main page result
    sourceData.mainPage?.result?.businessDetails?.companyName ||
    // Check raw business name
    (sourceData as any).businessName || 
    // Extract from title if all else fails
    sourceData.mainPage?.result?.extractedContent?.find(
      (item: any) => item.tag === 'title'
    )?.text?.split('|')?.[0]?.trim() ||
    // Default value
    'Unknown Business';
  
  // Extract duration/analysis time from different possible locations
  const analysisTime = 
    sourceData.analysisTime || 
    sourceData.duration || 
    data.analysisTime ||
    data.duration ||
    ((sourceData as any).metrics?.analysisTime) || 
    ((sourceData as any).metrics?.duration) ||
    ((data as any).metrics?.analysisTime) ||
    ((data as any).metrics?.duration) ||
    0;

  // Get main page URL
  const mainPageUrl = sourceData.mainPage?.url || sourceData.url || data.url || '';

  // Extract business description from either metadata or summary
  const businessDescription = 
    sourceData.summary?.overall || 
    sourceData.mainPage?.result?.metadata?.description || 
    sourceData.metadata?.description ||
    '';

  // Extract links/good URLs from various places
  const links = 
    sourceData.goodUrls || 
    sourceData.mainPage?.result?.goodUrls || 
    sourceData.pageResult?.goodUrls || 
    [];

  // Extract favicon information
  const favicon = sourceData.favicon || sourceData.mainPage?.result?.favicon || null;

  // Extract color scheme
  const colorScheme = sourceData.colorScheme || null;

  // Extract metadata
  const metadata = {
    title: sourceData.mainPage?.result?.metadata?.title || sourceData.metadata?.title || '',
    description: sourceData.mainPage?.result?.metadata?.description || sourceData.metadata?.description || '',
    pageTitle: sourceData.summary?.pageTitle || '',
    metaDescription: sourceData.summary?.metaDescription || '',
    keywords: sourceData.summary?.keywords || '',
    logoText: sourceData.summary?.logoText || businessName,
    openGraph: sourceData.mainPage?.result?.metadata?.openGraph || sourceData.metadata?.openGraph || {},
    twitter: sourceData.mainPage?.result?.metadata?.twitter || sourceData.metadata?.twitter || {}
  };

  // Now create the clean structure with the data we've extracted
  const normalizedResult: AnalysisResult = {
    requestId: sourceData.requestId || data.requestId || requestId,
    status: sourceData.status || data.status || 'ok',
    message: sourceData.message || data.message || 'Analysis completed',
    url: mainPageUrl,
    cachedAt: sourceData.cachedAt || data.cachedAt || new Date().toISOString(),
    analysisTime: analysisTime,
    
    // Business details
    businessDetails: {
      name: businessName,
      website: mainPageUrl,
      description: businessDescription,
      phone: sourceData.businessDetails?.phone || '',
      email: sourceData.businessDetails?.email || '',
      socialHandles: sourceData.businessDetails?.socialHandles || {},
      contact: sourceData.businessDetails?.contact || '',
    },
    
    // Metadata
    metadata: metadata,
    
    // Favicon
    favicon: favicon,
    
    // Color scheme
    colorScheme: colorScheme,
    
    // Pages structure
    pages: {
      main: {
        url: sourceData.mainPage?.url || '',
        text: sourceData.mainPage?.text || '',
        html: sourceData.mainPage?.html || '',
        images: sourceData.mainPage?.images || [],
        extractedContent: sourceData.extractedContent || 
                          sourceData.mainPage?.result?.extractedContent || []
      },
      secondary: sourceData.secondaryPages?.map((page: any) => ({
        url: page.url || '',
        text: page.text || '',
        html: page.html || '',
        images: page.images || [],
        extractedContent: page.result?.extractedContent || []
      })) || []
    },
    
    // Summary information
    summary: {
      businessName: sourceData.summary?.businessName || sourceData.summary?.companyName || '',
      overall: sourceData.summary?.overall || '',
      siteType: sourceData.summary?.siteType || '',
      jobsToDo: sourceData.summary?.jobsToDo || '',
      goal: sourceData.summary?.goal || '',
      features: sourceData.summary?.features || [],
      role: sourceData.summary?.role || '',
      personaVoice: sourceData.summary?.personaVoice || {
        tone: '',
        style: '',
        keyPhrases: [],
        emotionalTriggers: [],
        brandPersonality: ''
      },
      primaryChatGoal: sourceData.summary?.primaryChatGoal || '',
      primaryJobToDo: sourceData.summary?.primaryJobToDo || '',
      shortJobDescription: sourceData.summary?.shortJobDescription || '',
      customDiscoveryInstructions: sourceData.summary?.customDiscoveryInstructions || '',
      discoveryQuestions: sourceData.summary?.discoveryQuestions || [],
      pageTitle: sourceData.summary?.pageTitle || '',
      metaDescription: sourceData.summary?.metaDescription || '',
      keywords: sourceData.summary?.keywords || '',
      logoText: sourceData.summary?.logoText || '',
      painPoints: sourceData.summary?.painPoints || [],
      jobsToBeDone: sourceData.summary?.jobsToBeDone || [],
      personaCentricBenefits: sourceData.summary?.personaCentricBenefits || {},
      socialProof: sourceData.summary?.socialProof || [],
      offersAndGuarantees: sourceData.summary?.offersAndGuarantees || [],
      priorityKnowledge: sourceData.summary?.priorityKnowledge || [],
      commitmentLevel: sourceData.summary?.commitmentLevel || 'medium commitment',
      objections: sourceData.summary?.objections || []
    },
    
    // Links (previously goodUrls)
    links: links,
    
    // Backwards compatibility fields
    processLogs: sourceData.processLogs || [],
    ctaData: sourceData.ctaData || data.ctaData || {
      primaryCtaUrl: null,
      ctaType: '',
      ctaDescription: '',
      confidence: 'low'
    },
    jobToDoAction: sourceData.jobToDoAction,
    jobsToDoActions: sourceData.jobsToDoActions,
    errorCode: sourceData.errorCode || data.errorCode,
    widget: sourceData.widget || sourceData.mainPage?.result?.widget || {
      detected: false,
      type: 'None',
      details: []
    }
  };
  
  // For backward compatibility, set mainPage and secondaryPages separately
  // These will be marked as deprecated
  (normalizedResult as any).mainPage = normalizedResult.pages.main;
  (normalizedResult as any).secondaryPages = normalizedResult.pages.secondary;
  
  // For backward compatibility, set goodUrls
  (normalizedResult as any).goodUrls = normalizedResult.links;
  
  // For backward compatibility, set duration
  (normalizedResult as any).duration = normalizedResult.analysisTime;

  // Log the key parts of the structure for debugging
  console.log('Normalized structure:', {
    businessName: normalizedResult.businessDetails.name,
    requestId: normalizedResult.requestId,
    url: normalizedResult.url,
    pageCount: 1 + (normalizedResult.pages.secondary?.length || 0)
  });
  
  return normalizedResult;
} 